// react and mui stuff
import { useState, useContext, useEffect, useRef } from 'react';
import { ButtermelonContext, UserContext } from '../../utils/contexts';
import { ToastContext } from '../toast';
import { useNavigate } from 'react-router-dom';
import Markdown from "markdown-to-jsx"

import { useColorScheme } from '@mui/joy/styles';

import Box from "@mui/joy/Box"
import Typography from "@mui/joy/Typography"
import IconButton from "@mui/joy/IconButton"
import Avatar from "@mui/joy/Avatar"
import Tooltip from "@mui/joy/Tooltip"
import Dropdown from "@mui/joy/Dropdown"
import Menu from "@mui/joy/Menu"
import MenuButton from "@mui/joy/MenuButton"
import MenuItem from "@mui/joy/MenuItem"
import ListDivider from "@mui/joy/ListDivider"
import Drawer from "@mui/joy/Drawer"
import ModalClose from "@mui/joy/ModalClose"
import DialogTitle from "@mui/joy/DialogTitle"
import Autocomplete from "@mui/joy/Autocomplete"
import AutocompleteOption from "@mui/joy/AutocompleteOption"
import DialogContent from "@mui/joy/DialogContent"
import FormControl from "@mui/joy/FormControl"
import ListItemContent from "@mui/joy/ListItemContent"
import Modal from "@mui/joy/Modal"
import ModalDialog from "@mui/joy/ModalDialog"

import SearchRounded from "@mui/icons-material/SearchRounded"
import DarkModeRounded from "@mui/icons-material/DarkModeRounded"
import LightModeRounded from "@mui/icons-material/LightModeRounded"
import LogoutRounded from "@mui/icons-material/LogoutRounded"
import MenuRounded from "@mui/icons-material/MenuRounded" 

// custom components
import Navigation from './navigation';
import useBreakpoint from '../../utils/breakpoint';

// data and stuff
import { SearchedUser } from '../../types/users';
import { imgSrc, userAPI } from '../../utils/backend';
import { setData } from '../../utils/setdata';

function ColorSchemeToggle() {
  const { mode, setMode } = useColorScheme();
  const [mounted, setMounted] = useState(false);
  const { bm, setBm } = useContext(ButtermelonContext)
  const { setToast } = useContext(ToastContext)
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) {
    return <IconButton size="sm" variant="outlined" color="primary" />;
  }
  return (
    <Tooltip title="Change theme" variant="outlined">
      <IconButton
        data-screenshot="toggle-mode"
        size="sm"
        variant="plain"
        color="neutral"
        sx={{ alignSelf: 'center' }}
        onClick={() => {
          if (mode === 'light') {
            setMode('dark');
          } else {
            setMode('light');
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (!bm) setToast({ text: "Buttermelon theme enabled. (right click icon to disable)", color: "success" })
          setBm(!bm)
        }}
      >
        {mode === 'light' ? <DarkModeRounded/> : <LightModeRounded/>}
      </IconButton>
    </Tooltip>
  );
}

type SBProps = {
  customCloseAction?: Function
}

function SearchBar({ customCloseAction }: SBProps) {
  const [searchValue, setSearchValue] = useState("")
  const [prevSearch, setPrevSearch] = useState({ term: "", res: [] as SearchedUser[] });
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchedUser[]>([])
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const nav = useNavigate()
  const { setToast } = useContext(ToastContext)
  const { user, setUser } = useContext(UserContext)

  function search(v: string) {
    // don't bother searching again if it's the same thing
    if (v === prevSearch.term) return setSearchResults([...prevSearch.res])
    // look up!
    userAPI.search(v).then(res => {
      setLoading(false);
      if (!setData(res, setToast, user, setUser)) return;

      // set results! (and save the search)
      setSearchResults(res)
      setPrevSearch({ term: v, res })
    })
  }

  return (
    <FormControl error={open && searchValue.length < 3 && searchValue.length > 0}>
      <Autocomplete
        size="sm"
        variant="outlined"
        placeholder="Search for users..."
        name='Search for users'
        options={searchResults}
        inputValue={searchValue}
        onOpen={() => setOpen(true)}
        onClose={(_, reason) => {
          setOpen(false)
          if (customCloseAction && reason !== "blur") customCloseAction()
        }}
        open={open}
        disableClearable
        onInputChange={(e, v) => {
          // clear last timer
          clearTimeout(timerRef.current)
          // set the search
          setSearchValue(v)
          // clear the results
          setSearchResults([]);
          // if their search is long enough
          if (v.length > 2) {
            // gogogogogogo (after a second in case they're still typing)
            setLoading(true)
            timerRef.current = setTimeout(() => search(v), 1000)
          } else {
            // womp womp not long enough
            setLoading(false)
          }
        }}
        filterOptions={(o) => o}
        getOptionLabel={o => o.displayName}
        getOptionKey={o => o.id}
        loading={open && loading}
        startDecorator={<SearchRounded color="primary"/>}
        sx={{
          alignSelf: 'center',
          display: 'flex',
        }}
        renderOption={(props, option) => (
          <AutocompleteOption
            onClick={() => {
              // go to their profile page on click
              setOpen(false)
              if (customCloseAction) customCloseAction()
              nav(`../profile/${option.id}`)
            }} key={option.id}
          >
            <ListItemContent>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Avatar src={option.avatar} sx={{ marginRight: "5px" }}/>
                <Typography className="noTextOverflow"><Markdown>{option.displayName}</Markdown></Typography>
              </div>
              {option.igns && option.igns.map(i => (<Typography level='body-sm' key={i.system}><Markdown>{`${i.system}: ${i.ign}`}</Markdown></Typography>))}
            </ListItemContent>
          </AutocompleteOption>
        )}
      />
    </FormControl>
  )
}

type props = {
  setNavOpen: Function
  navOpen: boolean
}
export default function Header({ setNavOpen, navOpen }: props) {
  const {user, setUser} = useContext(UserContext)
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false)
  const { mode } = useColorScheme()
  const bp = useBreakpoint().getBreakPointName()
  const nav = useNavigate()
  return (
    <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'space-between' }}>
      {/** Mobile Search Bar */}
     <Modal
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        open={searching}
        onClose={() => setSearching(false)}
      >
        <ModalDialog>
          <ModalClose variant="plain" sx={{ m: 1 }} />
          <DialogTitle>User Lookup</DialogTitle>
          <DialogContent>Search for users by Discord username or IGN</DialogContent>
          <SearchBar customCloseAction={() => setSearching(false)}/>
        </ModalDialog>
      </Modal>
      <div style={{ display: "flex" }}>

        {/** Menu Toggle */}
        <IconButton variant="plain" color="neutral" onClick={() => {
          if (bp < 1) setOpen(true)
          else setNavOpen(!navOpen)
        }} aria-label='Toggle menu'>
          <MenuRounded/>
        </IconButton>

        {/** LDSG Logo */}
        <img src={imgSrc(`logos/ldsg-${mode === "light" ? "dark" : "light"}.svg`)}
          alt='LDSG Logo'
          style={{height: "50px", width: "172.5px", marginLeft: "5px", cursor: "pointer" }}
          onClick={() => nav("/profile")}
        />
      </div>
      {/** Mobile nav panel */}
      <Box sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
        <Drawer
          sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
          open={open}
          onClose={() => setOpen(false)}
        >
          <ModalClose />
          <DialogTitle>!profile</DialogTitle>
          <Box sx={{ px: 1, height: "100%" }}>
            {user && <Navigation setDrawerOpen={setOpen} />}
          </Box>
        </Drawer>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        {bp > 0 && <SearchBar/>}
        {bp < 1 && 
          <IconButton
            size="sm"
            variant="outlined"
            color="neutral"
            sx={{ alignSelf: 'center' }}
            onClick={() => setSearching(true)}
          >
            <SearchRounded/>
          </IconButton>
        }
        <ColorSchemeToggle />
        <Dropdown>
          <MenuButton
            name='User Menu'
            variant="plain"
            size="sm"
            sx={{ maxWidth: '32px', maxHeight: '32px', borderRadius: '9999999px' }}
          >
            <Avatar
              src={user?.avatar}
              slotProps={{ img: { rel: "preconnect"}}}
              sx={{ maxWidth: '32px', maxHeight: '32px' }}
              alt="User Menu"
            />
          </MenuButton>
          <Menu
            placement="bottom-end"
            size="sm"
            sx={{
              zIndex: '99999',
              p: 1,
              gap: 1,
              '--ListItem-radius': 'var(--joy-radius-sm)',
            }}
          >
            <MenuItem onClick={() => nav(`/profile/${user?.id}`)}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  slotProps={{ img: { rel: "preconnect"}}}
                  src={user?.avatar}
                  sx={{ borderRadius: '50%' }}
                  alt={"User Menu"}
                />
                <Box sx={{ ml: 1.5 }}>
                  <Typography level="title-sm" textColor="text.primary">
                    {user?.displayName}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
            <ListDivider />
            <MenuItem onClick={() => {
                // goodbye world
                userAPI.logout().then(() => {
                  setUser(null)
                  window.location.href = process.env.REACT_APP_FRONTEND as string
                })
            }}>
              <LogoutRounded/>
              Log out
            </MenuItem>
          </Menu>
        </Dropdown>
      </Box>
    </Box>
  );
}